import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2, User, Shield, CreditCard, FileText, Camera,
  ChevronRight, ArrowRight, Loader2, Heart, Phone, MapPin,
  Clock, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const C = { green: '#22c55e', greenBtn: '#16a34a', amber: '#f59e0b', red: '#ef4444', purple: '#6b5ce7', black: '#111' };

/* Mock appointment data (pre-personalized from reminder link) */
const APPOINTMENT = {
  name: 'Emily Davis',
  time: '10:30 AM',
  type: 'Follow-up',
  provider: 'Dr. Chen',
  date: 'Thursday, April 17, 2026',
  location: 'Valley Medical Associates — Main Office',
};

const DEMOGRAPHICS = {
  phone: '(555) 345-6789',
  email: 'emily.davis@email.com',
  address: '456 Pine Ave, Austin, TX 78702',
  emergencyContact: 'Mark Davis — (555) 456-7890',
  pharmacy: 'CVS Pharmacy — Congress Ave',
};

const INSURANCE = {
  payer: 'Aetna HMO',
  memberId: 'AET-554821',
  group: 'GRP-7891',
  copay: 20,
  status: 'Active',
};

const STEPS = [
  { id: 1, label: 'Verify', icon: User },
  { id: 2, label: 'Info', icon: MapPin },
  { id: 3, label: 'Insurance', icon: Shield },
  { id: 4, label: 'ID', icon: Camera },
  { id: 5, label: 'Forms', icon: FileText },
  { id: 6, label: 'Pay', icon: CreditCard },
];

const EchoPatientHub = () => {
  const { isDemo } = useAppMode();
  const [step, setStep] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const [infoConfirmed, setInfoConfirmed] = useState(false);
  const [insuranceConfirmed, setInsuranceConfirmed] = useState(false);
  const [idSkipped, setIdSkipped] = useState(false);
  const [formsComplete, setFormsComplete] = useState(false);
  const [paymentChoice, setPaymentChoice] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [processing, setProcessing] = useState(false);

  const nextStep = () => setStep(s => Math.min(s + 1, 7));

  const handleCheckIn = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setCheckedIn(true);
      toast.success('Check-in complete');
    }, 1500);
  };

  /* ═══ CHECKED IN — FINAL SCREEN ═══ */
  if (checkedIn) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="hub-checkin-complete">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center" style={{ background: '#f0fdf4' }}>
            <CheckCircle2 className="h-8 w-8" style={{ color: C.green }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">You're checked in!</h1>
            <p className="text-sm text-slate-500 mt-1">We'll call you shortly.</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Patient</span><span className="font-medium text-slate-800">{APPOINTMENT.name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Time</span><span className="font-medium text-slate-800">{APPOINTMENT.time}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Provider</span><span className="font-medium text-slate-800">{APPOINTMENT.provider}</span></div>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <Clock className="h-4 w-4" /> Estimated wait: <span className="font-semibold text-slate-800">~8 min</span>
          </div>
          <p className="text-xs text-slate-400">A receipt has been emailed to {DEMOGRAPHICS.email}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="patient-hub-page">
      {/* Header */}
      <header className="border-b border-slate-100 px-6 py-4 flex items-center justify-between" data-testid="hub-header">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: C.black }}>
            <Heart className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Valley Medical Associates</p>
            <p className="text-[10px] text-slate-400">Patient Check-In</p>
          </div>
        </div>
        <Badge variant="outline" className="text-[9px] border-emerald-200 text-emerald-600 bg-emerald-50 gap-1">
          <Shield className="h-2.5 w-2.5" /> HIPAA Secure
        </Badge>
      </header>

      {/* Progress bar */}
      <div className="px-6 py-3 border-b border-slate-50">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const done = step > s.id;
            return (
              <div key={s.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={cn("h-8 w-8 rounded-full flex items-center justify-center transition-colors",
                    done ? "bg-emerald-500" : active ? "bg-[#111]" : "bg-slate-100"
                  )}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-white" /> : <s.icon className={cn("h-3.5 w-3.5", active ? "text-white" : "text-slate-400")} />}
                  </div>
                  <span className={cn("text-[9px] mt-1 font-medium", active ? "text-slate-800" : done ? "text-emerald-600" : "text-slate-400")}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={cn("h-0.5 w-8 mx-1 rounded-full", done ? "bg-emerald-400" : "bg-slate-200")} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-6 py-8">

        {/* ═══ STEP 1: ARRIVE & VERIFY ═══ */}
        {step === 1 && (
          <div className="space-y-6" data-testid="hub-step-1">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Is this you?</h2>
              <p className="text-sm text-slate-500 mt-1">Confirm your appointment details</p>
            </div>
            <div className="p-5 rounded-xl border border-slate-200 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-800">{APPOINTMENT.name}</p>
                  <p className="text-xs text-slate-400">{APPOINTMENT.date}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100 text-sm">
                <div><span className="text-slate-400 text-xs">Time</span><p className="font-medium text-slate-800">{APPOINTMENT.time}</p></div>
                <div><span className="text-slate-400 text-xs">Visit Type</span><p className="font-medium text-slate-800">{APPOINTMENT.type}</p></div>
                <div><span className="text-slate-400 text-xs">Provider</span><p className="font-medium text-slate-800">{APPOINTMENT.provider}</p></div>
                <div><span className="text-slate-400 text-xs">Location</span><p className="font-medium text-slate-800 text-xs">{APPOINTMENT.location}</p></div>
              </div>
            </div>
            <Button className="w-full h-12 text-sm font-medium" style={{ background: C.greenBtn }} onClick={() => { setConfirmed(true); nextStep(); }} data-testid="hub-confirm-identity">
              Yes, that's me <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-xs text-slate-400 text-center">Not you? <button className="text-blue-500 underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Search by name & DOB</button></p>
          </div>
        )}

        {/* ═══ STEP 2: UPDATE PERSONAL INFO ═══ */}
        {step === 2 && (
          <div className="space-y-6" data-testid="hub-step-2">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Review your information</h2>
              <p className="text-sm text-slate-500 mt-1">Anything changed?</p>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Phone', value: DEMOGRAPHICS.phone, icon: Phone },
                { label: 'Email', value: DEMOGRAPHICS.email, icon: MapPin },
                { label: 'Address', value: DEMOGRAPHICS.address, icon: MapPin },
                { label: 'Emergency Contact', value: DEMOGRAPHICS.emergencyContact, icon: User },
                { label: 'Preferred Pharmacy', value: DEMOGRAPHICS.pharmacy, icon: Heart },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
                  <f.icon className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] text-slate-400">{f.label}</p>
                    <p className="text-sm text-slate-800">{f.value}</p>
                  </div>
                  <button className="text-[10px] text-blue-500 font-medium" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => toast.info(`Edit ${f.label}`)}>Edit</button>
                </div>
              ))}
            </div>
            <Button className="w-full h-12 text-sm font-medium" style={{ background: C.greenBtn }} onClick={() => { setInfoConfirmed(true); nextStep(); }} data-testid="hub-info-confirm">
              Looks good <CheckCircle2 className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* ═══ STEP 3: INSURANCE REVIEW ═══ */}
        {step === 3 && (
          <div className="space-y-6" data-testid="hub-step-3">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Insurance on file</h2>
              <p className="text-sm text-slate-500 mt-1">Is this still your active insurance?</p>
            </div>
            <div className="p-5 rounded-xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold text-slate-800">{INSURANCE.payer}</p>
                <Badge className="text-[9px] border-0 bg-emerald-50 text-emerald-700">{INSURANCE.status} <CheckCircle2 className="h-2.5 w-2.5 ml-0.5" /></Badge>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-400 text-[10px]">Member ID</span><p className="font-medium text-slate-800 text-xs">{INSURANCE.memberId}</p></div>
                <div><span className="text-slate-400 text-[10px]">Group</span><p className="font-medium text-slate-800 text-xs">{INSURANCE.group}</p></div>
                <div><span className="text-slate-400 text-[10px]">Copay</span><p className="font-medium text-slate-800 text-xs">${INSURANCE.copay}</p></div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 h-12 text-sm font-medium" style={{ background: C.greenBtn }} onClick={() => { setInsuranceConfirmed(true); nextStep(); }} data-testid="hub-insurance-same">
                Yes, same insurance <CheckCircle2 className="h-4 w-4 ml-2" />
              </Button>
              <Button variant="outline" className="h-12 text-sm gap-1.5" onClick={() => { toast.info('Opening camera for new insurance card...'); setInsuranceConfirmed(true); nextStep(); }} data-testid="hub-insurance-changed">
                <Camera className="h-4 w-4" /> Changed
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: ID SCAN ═══ */}
        {step === 4 && (
          <div className="space-y-6" data-testid="hub-step-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Photo ID verification</h2>
              <p className="text-sm text-slate-500 mt-1">For returning patients, this step is optional</p>
            </div>
            <div className="p-8 rounded-xl border-2 border-dashed border-slate-200 text-center">
              <Camera className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-1">Take a photo of your ID</p>
              <p className="text-xs text-slate-400">We'll extract your info automatically</p>
              <Button variant="outline" className="mt-4 gap-1.5" onClick={() => toast.info('Camera opening...')} data-testid="hub-scan-id">
                <Camera className="h-4 w-4" /> Open Camera
              </Button>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-12 text-sm" onClick={() => { setIdSkipped(true); nextStep(); }} data-testid="hub-skip-id">
                Skip for now
              </Button>
              <Button className="flex-1 h-12 text-sm font-medium" style={{ background: C.black }} onClick={() => { nextStep(); }} data-testid="hub-id-done">
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 5: PRE-VISIT FORMS ═══ */}
        {step === 5 && (
          <div className="space-y-6" data-testid="hub-step-5">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Pre-visit forms</h2>
              <p className="text-sm text-slate-500 mt-1">Complete required forms before your visit</p>
            </div>
            <div className="space-y-2">
              {[
                { name: 'Health History Update', required: true, completed: true },
                { name: 'Consent for Treatment', required: true, completed: false },
                { name: 'Medication List Review', required: false, completed: false },
              ].map((form, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" data-testid={`hub-form-${i}`}>
                  {form.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-slate-200 shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{form.name}</p>
                    <p className="text-[10px] text-slate-400">{form.required ? 'Required' : 'Optional'} {form.completed ? '· Complete' : ''}</p>
                  </div>
                  {!form.completed && (
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => toast.info(`Opening ${form.name}...`)}>Fill out</Button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-400 px-1">
              <Sparkles className="h-3 w-3 text-violet-500" /> Pre-filled where possible from your chart
            </div>
            <Button className="w-full h-12 text-sm font-medium" style={{ background: C.black }} onClick={() => { setFormsComplete(true); nextStep(); }} data-testid="hub-forms-continue">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* ═══ STEP 6: CO-PAY PAYMENT ═══ */}
        {step === 6 && (
          <div className="space-y-6" data-testid="hub-step-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900">Co-pay payment</h2>
              <p className="text-sm text-slate-500 mt-1">Your co-pay today is <span className="font-semibold text-slate-800">${INSURANCE.copay}</span></p>
            </div>
            <div className="space-y-2">
              {[
                { id: 'card_file', label: 'Card on file', sub: 'Visa ending in 4242', icon: CreditCard },
                { id: 'new_card', label: 'New card', sub: 'Add a payment method', icon: CreditCard },
                { id: 'apple_pay', label: 'Apple Pay / Google Pay', sub: 'Tap to pay', icon: Phone },
                { id: 'at_desk', label: 'Pay at front desk', sub: "I'll pay when I arrive", icon: User },
              ].map(opt => (
                <button key={opt.id} onClick={() => setPaymentChoice(opt.id)}
                  className={cn("w-full flex items-center gap-3 p-4 rounded-lg border transition-colors text-left",
                    paymentChoice === opt.id ? "border-[#111] bg-slate-50" : "border-slate-100 hover:border-slate-300"
                  )} style={{ cursor: 'pointer' }} data-testid={`hub-pay-${opt.id}`}>
                  <opt.icon className={cn("h-5 w-5 shrink-0", paymentChoice === opt.id ? "text-slate-800" : "text-slate-400")} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{opt.label}</p>
                    <p className="text-[10px] text-slate-400">{opt.sub}</p>
                  </div>
                  <div className={cn("h-5 w-5 rounded-full border-2 flex items-center justify-center",
                    paymentChoice === opt.id ? "border-[#111]" : "border-slate-200"
                  )}>
                    {paymentChoice === opt.id && <div className="h-2.5 w-2.5 rounded-full bg-[#111]" />}
                  </div>
                </button>
              ))}
            </div>
            <Button className="w-full h-12 text-sm font-medium" style={{ background: C.greenBtn }}
              disabled={!paymentChoice || processing}
              onClick={handleCheckIn}
              data-testid="hub-complete-checkin">
              {processing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : <>Complete Check-In <CheckCircle2 className="h-4 w-4 ml-2" /></>}
            </Button>
            <p className="text-[10px] text-slate-400 text-center">Receipt will be emailed to {DEMOGRAPHICS.email}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EchoPatientHub;
